import { BrButton, BrCard, BrDivider, BrInput, BrLoading, BrSelect, Col, Row } from '@govbr-ds/react-components';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import AnmSelectQuery from 'components/anmSelectQuery/anmSelectQuery';
import AnmSwitch from 'components/anmSwitch/anmSwitch';
import BaseDeCalculoAccordion from 'components/baseDeCalculoAccordion/baseDeCalculoAccordion';
import ErrorMessage from 'components/errorMessage/errorMessage';
import SecaoDeducoes from 'components/secaoDeducoes/secaoDeducoes';
import { ArrecadacaoContext } from 'contexts/ArrecadacaoContext';
import { useNotificacao } from 'hooks/useNotificacao';
import {
  IEstrutura,
  IEstruturaEnviar,
  IFerrovia,
  IFerroviaEnviar,
  IOperacaoPortuaria,
  IOperacaoPortuariaEnviar,
} from 'interfaces/IAfetacoes';
import { IDeducaoAdicionada, IDeducoes } from 'interfaces/IDeducao';
import { IErrorWithSentryId } from 'interfaces/IErrorWithSentryId';
import { INcmListagem } from 'interfaces/INcm';
import {
  INcmPrimeiroAdquirenteEnviar,
  INcmPrimeiroAdquirenteLista,
  INcmPrimeiroAdquirenteReceber,
} from 'interfaces/IPrimeiroAdquirente';
import { ITipoDeUsoLista } from 'interfaces/ITipodeUso';
import { useBackEndRequests as backendPrimeiroAdquirente } from 'pages/PrimeiroAdquirente/lib/backEndRequests';
import ModalidadeAfetacao from 'pages/TitularArrendatario/TitularArrendatarioEdit/components/outrasBaseCalculoForm/modalidadeAfetacao/modalidadeAfetacaoForm';
import { useBackEndRequests as backEndGeral } from 'pages/components/lib/backEndRequests';
import React, { useContext, useEffect, useState } from 'react';
import { Table } from 'react-dsgov';
import { Tooltip } from 'react-tooltip';
import { converterDeducoesParaArrayString } from 'utils/converterDeducoesParaString';
import { formatCurrency, removeFormatCurrency } from 'utils/formatCurrency';
import { formatNumber } from 'utils/formatNumber';
import { formatPercentual } from 'utils/formatPercentual';
import { getHintText } from 'utils/getHintText';
import styles from './dadosOperacoes.module.scss';
import { validarDadosOperacoesForm } from './dadosOperacoesValidacaoForm';

interface IDadosOperacoesProps {
  isEdicao: boolean;
  setIsEdicao: React.Dispatch<React.SetStateAction<boolean>>;
  isViewOnly?: boolean;
}

const DadosOperacoes = ({ isViewOnly, isEdicao, setIsEdicao }: IDadosOperacoesProps) => {
  const urlParams = new URLSearchParams(window.location.search);
  const guiaRecolhimentoId = urlParams.get('id') || '';
  const queryClient = useQueryClient();

  const [isRejeito, setIsRejeito] = useState<boolean>(false);
  const [codigoNCM, setCodigoNCM] = useState<string>('');
  const [valorAquisicao, setValorAquisicao] = useState<string>('');
  const [quantidade, setQuantidade] = useState<string>('');
  const [unidadeMedida, setUnidadeMedida] = useState<string>('');
  const [deducoesAdicionadas, setDeducoesAdicionadas] = useState<IDeducaoAdicionada[]>([]);
  const [haCompraTerceiro, setHaCompraTerceiro] = useState<boolean>(false);

  const [hasUsoEspecifico, setHasUsoEspecifico] = useState(false);
  const [tipoUso, setTipoUso] = useState<number>(-1);
  const [placeHolderTipoUso, setPlaceHolderTipoUso] = useState<string>('Selecione o tipo de uso');
  const [ncmEdicao, setNcmEdicao] = useState<INcmPrimeiroAdquirenteReceber | null>(null);

  const [hasFerrovia, setHasFerrovia] = useState(false);
  const [hasDutovia, setHasDutovia] = useState(false);
  const [hasOperacoesPortuarias, setHasOperacoesPortuarias] = useState(false);
  const [hasEstruturasFora, setHasEstruturasFora] = useState(false);

  const [ferroviasAdicionadas, setFerroviasAdicionadas] = useState<IFerrovia[] | []>([]);
  const [operacoesPortuariasAdicionadas, setOperacoesPortuariasAdicionadas] = useState<IOperacaoPortuaria[] | []>([]);
  const [estruturasAdicionadas, setEstruturasAdicionadas] = useState<IEstrutura[] | []>([]);

  const [isLoadingSalvarNcm, setIsLoadingSalvarNcm] = useState<boolean>(false);

  const { getNcmPorGuiaRecolhimento, getUnidadesMedidaPorGuiaRecolhimento, getIsPadraoAguaMineral, getTiposDeUso } =
    backEndGeral();

  const { getListaNCM, deleteNCM, postNCM, putNCM, getDeducoes } = backendPrimeiroAdquirente();

  const mostrarNotificacao = useNotificacao();

  const context = useContext(ArrecadacaoContext);

  const {
    data: listaNcmData,
    isFetching: listaNcmIsLoading,
    refetch: listaNcmRefetch,
    error: listaNcmError,
  } = useQuery<INcmPrimeiroAdquirenteLista, IErrorWithSentryId>({
    queryKey: ['getListaNCM', guiaRecolhimentoId],
    queryFn: () => getListaNCM(guiaRecolhimentoId),
    refetchOnWindowFocus: false,
    retryDelay: 100,
    enabled: !!guiaRecolhimentoId && guiaRecolhimentoId !== '',
  });

  const {
    data: deducoesData,
    isLoading: deducoesIsLoading,
    error: deducoesError,
    refetch: deducoesRefetch,
  } = useQuery<IDeducoes, IErrorWithSentryId>({
    queryKey: ['deducoesOutrasBasesCalculoQuery', guiaRecolhimentoId],
    queryFn: () => getDeducoes(guiaRecolhimentoId || ''),
    refetchOnWindowFocus: false,
  });

  const {
    data: isPadraoAguaMineral,
    isLoading: isPadraoAguaMineralIsLoading,
    error: isPadraoAguaMineralError,
    refetch: isPadraoAguaMineralRefetch,
  } = useQuery<boolean, IErrorWithSentryId>({
    queryKey: ['isPadraoAguaMineralQuery', guiaRecolhimentoId],
    queryFn: () => getIsPadraoAguaMineral(guiaRecolhimentoId),
    refetchOnWindowFocus: false,
  });

  const {
    data: tiposUsoData,
    isFetching: tiposUsoIsLoading,
    error: tiposUsoError,
    refetch: tiposUsoRefetch,
  } = useQuery<ITipoDeUsoLista, IErrorWithSentryId>({
    queryKey: ['tiposUsoQuery', guiaRecolhimentoId],
    queryFn: () => getTiposDeUso(guiaRecolhimentoId),
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    context?.setIdNCMPrimeiroAdquirente(-1);
  }, []);

  useEffect(() => {
    if (isPadraoAguaMineral === true) {
      setTipoUso(3);
      setPlaceHolderTipoUso('Águas minerais');
    }
  }, [isPadraoAguaMineral]);

  useEffect(() => {
    if (hasUsoEspecifico === false && !!tiposUsoData?.tipoDeUsoNaoEspecifico) {
      console.log('setando tipo de uso para não específico', tiposUsoData.tipoDeUsoNaoEspecifico[0]?.id);
      setTipoUso(tiposUsoData.tipoDeUsoNaoEspecifico[0]?.id);
    }
  }, []);

  useEffect(() => {
    if (isEdicao && hasUsoEspecifico === ncmEdicao?.hasUsoEspecifico) {
      setTipoUso(ncmEdicao?.tipoDeUso?.id);
      setPlaceHolderTipoUso(ncmEdicao?.tipoDeUso?.nome || 'Selecione o tipo de uso');
      setNcmEdicao(null);
    } else if (!hasUsoEspecifico && !!tiposUsoData?.tipoDeUsoNaoEspecifico) {
      setTipoUso(tiposUsoData.tipoDeUsoNaoEspecifico[0]?.id);
      setPlaceHolderTipoUso(tiposUsoData.tipoDeUsoNaoEspecifico[0]?.nome || 'Selecione o tipo de uso');
    } else {
      setTipoUso(-1);
      setPlaceHolderTipoUso('Selecione o tipo de uso');
    }

    if (isPadraoAguaMineral) {
      setTipoUso(3);
      setPlaceHolderTipoUso('Águas minerais');
    }
  }, [hasUsoEspecifico, isEdicao, tiposUsoData]);

  const limparFormulario = () => {
    setCodigoNCM('');
    setValorAquisicao('');
    setQuantidade('');
    setUnidadeMedida('');
    setIsRejeito(false);
    setDeducoesAdicionadas([]);
    setHasUsoEspecifico(false);
    setFerroviasAdicionadas([]);
    setOperacoesPortuariasAdicionadas([]);
    setEstruturasAdicionadas([]);
    setHasDutovia(false);
    setHasEstruturasFora(false);
    setHasFerrovia(false);
    setHasOperacoesPortuarias(false);
    setTipoUso(isPadraoAguaMineral === true ? 3 : -1);
  };

  const editarNcm = async (ncm: INcmPrimeiroAdquirenteReceber) => {
    setNcmEdicao(ncm);
    limparFormulario();
    context?.setIdNCMPrimeiroAdquirente(ncm.id);
    setCodigoNCM(ncm.codigoNcm);
    setValorAquisicao(ncm.valorAquisicao.toString());
    setQuantidade(ncm.quantidade.toString());
    setUnidadeMedida(ncm.unidadeMedida);
    setIsRejeito(ncm.isRejeito);
    setIsEdicao(true);
    setDeducoesAdicionadas(
      ncm.tributosIncidentes.map((deducao) => ({ nome: deducao.nome, valor: deducao.valor ?? 0 }))
    );
    setHasUsoEspecifico(ncm.hasUsoEspecifico);
    setFerroviasAdicionadas(
      ncm?.modalidadesAfetacaoFerrovia?.map((modalidade) => ({
        idFerrovia: modalidade.ferrovia.id,
        nomeFerrovia: modalidade.ferrovia.nome,
        origem: modalidade.municipioOrigem,
        destino: modalidade.municipioDestino,
      })) || []
    );
    setOperacoesPortuariasAdicionadas(
      ncm?.modalidadesAfetacaoPorto?.map((operacao) => ({
        idPorto: operacao.porto.id,
        municipio: operacao.porto.uf + ' - ' + operacao.porto.cidade,
      })) || []
    );
    setEstruturasAdicionadas(
      ncm?.modalidadesAfetacaoMunicipioMineracao?.map((estrutura) => ({
        codigoIBGE: estrutura.municipioMineracao.codigoIBGE,
        uf: estrutura.municipioMineracao.uf,
        nome: estrutura.municipioMineracao.nome,
      })) || []
    );
    setHasDutovia(ncm?.possuiAfetacaoDutovia);
    setHasEstruturasFora(
      ncm?.modalidadesAfetacaoMunicipioMineracao && ncm.modalidadesAfetacaoMunicipioMineracao.length > 0
    );
    setHasFerrovia(ncm?.modalidadesAfetacaoFerrovia && ncm.modalidadesAfetacaoFerrovia.length > 0);
    setHasOperacoesPortuarias(ncm?.modalidadesAfetacaoPorto && ncm.modalidadesAfetacaoPorto.length > 0);
  };

  const deletarNcm = async (ncmId: number) => {
    await deleteNCM(ncmId)
      .then(() => {
        mostrarNotificacao({
          tipo: 'success',
          conteudo: 'NCM excluído com sucesso',
        });
        if (listaNcmRefetch) {
          // Limpar a query pois o refetch estava trazendo a última consulta válida quando a chamada é feita e não há itens de ncm adicionados
          queryClient.resetQueries({
            queryKey: ['getListaNCM', guiaRecolhimentoId],
          });
          listaNcmRefetch();
        }
      })
      .catch((err) => {
        mostrarNotificacao({
          tipo: 'danger',
          conteudo: 'Erro ao excluir a guia de recolhimento, tente novamente mais tarde. Erro: ' + err.message,
        });
      });
  };

  const salvarBaseCalculoNCM = async () => {
    setIsLoadingSalvarNcm(true);
    const baseDeCalculoNCM: INcmPrimeiroAdquirenteEnviar = {
      idGuiaRecolhimento: guiaRecolhimentoId,
      codigoNcm: codigoNCM,
      unidadeMedida: unidadeMedida,
      valorAquisicao: removeFormatCurrency(valorAquisicao),
      quantidade: parseFloat(quantidade),
      isRejeito: isRejeito,
      tributosIncidentes: deducoesAdicionadas,
      tipoDeUso: parseInt(tipoUso.toString()),
      hasUsoEspecifico: hasUsoEspecifico, // valor do input de quantidade
      possuiAfetacaoDutovia: hasDutovia,
      modalidadesAfetacaoFerrovia: hasFerrovia
        ? ferroviasAdicionadas.map(
            (ferrovia): IFerroviaEnviar => ({
              idFerrovia: ferrovia.idFerrovia,
              municipioOrigem: ferrovia.origem.codigoIBGE,
              municipioDestino: ferrovia.destino.codigoIBGE,
            })
          )
        : [],
      modalidadesAfetacaoPorto: hasOperacoesPortuarias
        ? operacoesPortuariasAdicionadas.map(
            (operacao): IOperacaoPortuariaEnviar => ({
              idPorto: operacao.idPorto,
            })
          )
        : [],
      modalidadesAfetacaoMunicipioMineracao: hasEstruturasFora
        ? estruturasAdicionadas.map(
            (estrutura): IEstruturaEnviar => ({
              municipioMineracao: estrutura.codigoIBGE,
            })
          )
        : [],
    };

    if (listaNcmData?.itens.some((ncm) => ncm.codigoNcm === codigoNCM) && !isEdicao) {
      mostrarNotificacao({
        tipo: 'danger',
        conteudo: 'NCM já existe na lista, por favor escolha outro código NCM.',
      });
      setIsLoadingSalvarNcm(false);
      return;
    }

    const msgErroValidacao = validarDadosOperacoesForm(
      baseDeCalculoNCM,
      converterDeducoesParaArrayString(deducoesData),
      haCompraTerceiro
    );

    if (msgErroValidacao != '') {
      mostrarNotificacao({
        tipo: 'danger',
        conteudo: msgErroValidacao,
      });
      setIsLoadingSalvarNcm(false);
      return;
    }

    if (isEdicao) {
      await putNCM({ ...baseDeCalculoNCM, id: context?.idNCMPrimeiroAdquirente })
        .then(() => {
          limparFormulario();
          mostrarNotificacao({
            tipo: 'success',
            conteudo: `NCM salvo com sucesso.`,
          });
          listaNcmRefetch();
          setIsEdicao(false);
          context?.setIdNCMPrimeiroAdquirente(-1);
        })
        .catch((err) => {
          mostrarNotificacao({
            tipo: 'danger',
            conteudo: `Erro ao salvar NCM, tente novamente.`,
            ...(err && { complementoSentryId: err }),
          });
        })
        .finally(() => {
          setIsLoadingSalvarNcm(false);
          listaNcmRefetch();
        });
    } else {
      await postNCM(baseDeCalculoNCM)
        .then(() => {
          limparFormulario();
          mostrarNotificacao({
            tipo: 'success',
            conteudo: `NCM adicionado com sucesso.`,
          });
          listaNcmRefetch();
        })
        .catch((err) => {
          mostrarNotificacao({
            tipo: 'danger',
            conteudo: `Erro ao adicionar NCM, tente novamente.`,
            ...(err && { complementoSentryId: err }),
          });
        })
        .finally(() => {
          setIsLoadingSalvarNcm(false);
          listaNcmRefetch();
        });
    }
  };

  const isTipoDeUsoEspecificoDisabled = (tiposUsoData as ITipoDeUsoLista)?.tipoDeUsoEspecifico.length === 0;

  return (
    <div className={styles.container}>
      <div className={styles.cardContainer}>
        <BrCard className={styles.card}>
          <div className={styles.cardContent}>
            <span className={styles.cardTitle}>Valor Total de Aquisição (R$)</span>
            <span className={styles.cardValue}>{formatCurrency(listaNcmData?.totalValorAquisicoes || 0)}</span>
          </div>
        </BrCard>
        <BrCard className={styles.card}>
          <div className={styles.cardContent}>
            <span className={styles.cardTitle}>Quantidade Total</span>
            <span className={styles.cardValue}>{formatNumber(listaNcmData?.totalQuantidade || 0)}</span>
          </div>
        </BrCard>
        <BrCard className={styles.card}>
          <div className={styles.cardContent}>
            <span className={styles.cardTitle}>CFEM apurada no mês de ocorrência do fato gerador</span>
            <span className={styles.cardValue}>{formatCurrency(listaNcmData?.totalValorCfem || 0)}</span>
          </div>
        </BrCard>
      </div>
      <div className={styles.componentesTitle}>Componentes da Base de Cálculo da CFEM</div>
      <div className={styles.componentesContainer}>
        {!isViewOnly && (
          <BrCard className={styles.cardSecao}>
            {isPadraoAguaMineralIsLoading ? (
              <BrLoading large />
            ) : (
              <div className={styles.cardSecaoContent}>
                <div className={styles.cardSecaoTitle}>{isEdicao ? 'Editar' : 'Incluir'} um componente</div>

                <div className={styles.formContainer}>
                  <Row>
                    <Col>
                      <div data-testid='select-codigo-ncm'>
                        <AnmSelectQuery
                          customClassName={styles.select}
                          label='Código NCM'
                          value={codigoNCM}
                          setValue={setCodigoNCM}
                          functionQuery={() => getNcmPorGuiaRecolhimento(guiaRecolhimentoId)}
                          keyQuery={['getNcmPorGuiaRecolhimento', guiaRecolhimentoId]}
                          formatResult={(result: INcmListagem[]) =>
                            result.map((ncm: INcmListagem) => ({
                              label: ncm.codigo + ' - ' + ncm.descricao,
                              value: ncm.codigo,
                            }))
                          }
                          placeholder='Selecione o NCM'
                          hintText={getHintText('NCM')}
                        />
                      </div>
                    </Col>
                    <Col>
                      <BrInput
                        label='Valor de Aquisição (R$)'
                        autoComplete='off'
                        numeric={{
                          prefix: 'R$ ',
                          thousandSeparator: '.',
                          decimalScale: 2,
                          fixedDecimalScale: false,
                        }}
                        className={styles.input}
                        value={valorAquisicao}
                        onChange={(e) => setValorAquisicao(e.target.value)}
                        data-testid='input-valor-aquisicao'
                      />
                    </Col>
                    <Col>
                      <BrInput
                        label='Quantidade'
                        value={quantidade}
                        onChange={(e) => setQuantidade(e.target.value.replace(',', '.'))}
                        numeric={{
                          decimalScale: 2,
                          fixedDecimalScale: false,
                        }}
                        data-testid='input-quantidade'
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <div className={styles.label}>Preço Unitário (R$)</div>
                      <div className={styles.valor}>
                        <span className={styles.text}>
                          {valorAquisicao && quantidade
                            ? formatCurrency(removeFormatCurrency(valorAquisicao) / Number(quantidade))
                            : '-'}
                        </span>
                      </div>
                    </Col>
                    <Col>
                      <div data-testid='select-unidade-medida'>
                        <AnmSelectQuery
                          customClassName={styles.selectUnidadeMedida}
                          label='Unidade de Medida'
                          value={unidadeMedida}
                          setValue={setUnidadeMedida}
                          functionQuery={() => getUnidadesMedidaPorGuiaRecolhimento(guiaRecolhimentoId)}
                          keyQuery={['getUnidadesMedidaPorGuiaRecolhimento', guiaRecolhimentoId]}
                          formatResult={(result: string[]) =>
                            result.map((unidade: string) => ({
                              label: unidade,
                              value: unidade,
                            }))
                          }
                          placeholder='Selecione a unidade de medida'
                        />
                      </div>
                    </Col>
                    <Col></Col>
                  </Row>
                  <div className={styles.containerDeducoes}>
                    {deducoesIsLoading ? (
                      <BrLoading />
                    ) : deducoesError ? (
                      <ErrorMessage
                        errorMessage={'Erro ao carregar a lista de deduções. Por favor, tente novamente.'}
                        onClickFunction={() => {
                          deducoesRefetch();
                        }}
                      />
                    ) : (
                      deducoesData && (
                        <SecaoDeducoes
                          key={codigoNCM}
                          deducoesPossiveis={deducoesData}
                          deducoesAdicionadas={deducoesAdicionadas}
                          setDeducoesAdicionadas={setDeducoesAdicionadas}
                          setHaCompraTerceiro={setHaCompraTerceiro}
                          haCompraTerceiro={haCompraTerceiro}
                          semMargem
                        />
                      )
                    )}
                  </div>
                  <ModalidadeAfetacao
                    ferroviasAdicionadas={ferroviasAdicionadas}
                    setFerroviasAdicionadas={setFerroviasAdicionadas}
                    operacoesPortuariasAdicionadas={operacoesPortuariasAdicionadas}
                    setOperacoesPortuariasAdicionadas={setOperacoesPortuariasAdicionadas}
                    estruturasAdicionadas={estruturasAdicionadas}
                    setEstruturasAdicionadas={setEstruturasAdicionadas}
                    hasFerrovia={hasFerrovia}
                    setHasFerrovia={setHasFerrovia}
                    hasDutovia={hasDutovia}
                    setHasDutovia={setHasDutovia}
                    hasOperacoesPortuarias={hasOperacoesPortuarias}
                    setHasOperacoesPortuarias={setHasOperacoesPortuarias}
                    hasEstruturasFora={hasEstruturasFora}
                    setHasEstruturasFora={setHasEstruturasFora}
                  />
                  {isPadraoAguaMineralError ? (
                    <ErrorMessage
                      errorMessage={'Erro ao carregar dados. Por favor, tente novamente.'}
                      onClickFunction={() => {
                        isPadraoAguaMineralRefetch();
                      }}
                    />
                  ) : (
                    <div className={styles.criteriosUso}>
                      <div className={styles.titulo}>
                        <span>Critérios de Uso</span>
                      </div>
                      <div className={styles.divider}>
                        <BrDivider />
                      </div>
                      <div className={styles.campos}>
                        <div className={styles.camposContainer}>
                          <div
                            className={styles.switchContainer}
                            data-testid='switch-rejeito'
                            data-tooltip-id='rejeitos-tooltip'
                          >
                            <AnmSwitch
                              label='O bem mineral é oriundo de rejeitos e estéreis de minerais associados utilizados em outras cadeias produtivas?'
                              value={isRejeito}
                              setValue={setIsRejeito}
                              disable={isPadraoAguaMineral === true}
                              tooltipText='Informação vinculada à redução da alíquota da CFEM em 50%, conforme § 7º, do Art. 6º, da Lei nº 7.990/1989. Os procedimentos para aproveitamento de rejeitos e estéreis estão dispostos na RESOLUÇÃO ANM Nº 85, DE 2 DE DEZEMBRO DE 2021.'
                            />
                          </div>
                          <div className={styles.selectContainer}> </div>
                        </div>
                        {tiposUsoIsLoading === true && <BrLoading />}
                        {tiposUsoError == null && tiposUsoIsLoading === false && (
                          <div className={styles.camposContainer}>
                            <div className={styles.switchContainer} data-tooltip-id='tipo-uso-tooltip'>
                              <AnmSwitch
                                label='A substância possui uso específico?'
                                value={hasUsoEspecifico}
                                setValue={setHasUsoEspecifico}
                                tooltipText={
                                  'Essa marcação só deve ser feita quando o bem mineral for destinado ao uso imediato na construção civil, para fins de aplicação da alíquota de 1% (um por cento), nos termos do ANEXO da Lei 8.001/1990.'
                                }
                                disable={isTipoDeUsoEspecificoDisabled || isPadraoAguaMineral === true}
                              />
                            </div>
                            <form className={styles.selectContainer} autoComplete='off'>
                              <BrSelect
                                label={hasUsoEspecifico ? 'Tipo de Uso Específico' : 'Tipo de Uso Não Específico'}
                                key={`select-tipo-uso-${tipoUso}`} // força reset do select ao mudar entre uso específico e não específico
                                className={styles.select}
                                placeholder={placeHolderTipoUso}
                                value={tipoUso}
                                onChange={(value: number) => {
                                  setTipoUso(value);
                                }}
                                options={
                                  hasUsoEspecifico
                                    ? (tiposUsoData as ITipoDeUsoLista).tipoDeUsoEspecifico.map((tipo) => ({
                                        label: tipo.nome,
                                        value: tipo.id,
                                      }))
                                    : (tiposUsoData as ITipoDeUsoLista).tipoDeUsoNaoEspecifico.map((tipo) => ({
                                        label: tipo.nome,
                                        value: tipo.id,
                                      }))
                                }
                                disabled={isPadraoAguaMineral === true}
                              />
                            </form>
                          </div>
                        )}
                        {tiposUsoError && (
                          <div className={styles.errorContainer}>
                            <span className={styles.errorText}>Não foi possível carregar os dados</span>
                            <BrButton size='small' onClick={() => tiposUsoRefetch()}>
                              (Tentar novamente)
                            </BrButton>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  <div className={styles.rodape}>
                    <div className={styles.button}>
                      <BrButton
                        secondary
                        icon='plus'
                        loading={isLoadingSalvarNcm}
                        onClick={() => salvarBaseCalculoNCM()}
                      >
                        {isEdicao ? 'Editar' : 'Incluir'} Componente
                      </BrButton>
                    </div>
                  </div>
                </div>
                <Tooltip
                  opacity={1}
                  id='rejeitos-tooltip'
                  place='bottom'
                  className={styles.tooltip}
                  variant='warning'
                  disableTooltip={() => isPadraoAguaMineral !== true}
                >
                  <div className={styles.tooltipText}>
                    Essa opção está marcada como "Não" pois a substância Água Mineral não pode ser classificada como bem
                    mineral oriundo de rejeitos e estéreis.
                  </div>
                </Tooltip>
                <Tooltip
                  opacity={1}
                  id='tipo-uso-tooltip'
                  place='bottom'
                  className={styles.tooltip}
                  variant='warning'
                  disableTooltip={() => !(isTipoDeUsoEspecificoDisabled || isPadraoAguaMineral === true)}
                >
                  <div className={styles.tooltipText}>
                    Essa opção está marcada como "Não" pois a substância mineral selecionada não se enquadra na condição
                    "destinada ao uso imediato na construção civil" ou "rochas ornamentais".
                  </div>
                </Tooltip>
              </div>
            )}
          </BrCard>
        )}
        {isEdicao ? (
          <div className={styles.whiteSpace} />
        ) : (
          <BrCard className={styles.cardSecao}>
            <div className={styles.cardSecaoContent}>
              <div className={styles.cardSecaoTitle}>Lista de componentes adicionados</div>
              {listaNcmIsLoading ? (
                <div data-testid='loading'>
                  <BrLoading large />
                </div>
              ) : (
                <>
                  {listaNcmError && listaNcmError.status !== 404 ? (
                    <ErrorMessage
                      errorMessage={'Erro ao carregar a lista de NCM. Por favor, tente novamente.'}
                      onClickFunction={() => {
                        listaNcmRefetch();
                      }}
                    />
                  ) : (
                    <div className={styles.listaContainer}>
                      {!listaNcmData || listaNcmData.itens.length === 0 || listaNcmError?.status === 404 ? (
                        <p className={styles.emptyText}>Nenhum componente foi adicionado</p>
                      ) : (
                        <>
                          {listaNcmData?.itens.map((ncm: INcmPrimeiroAdquirenteReceber) => (
                            <BaseDeCalculoAccordion
                              title={'Código NCM: ' + ncm.codigoNcm}
                              deleteFunction={async () => deletarNcm(ncm?.id)}
                              editFunction={() => editarNcm(ncm)}
                              isNCM
                              isEditable={!isViewOnly}
                              key={ncm?.id}
                            >
                              <Row className={styles.contentRow}>
                                <Col>
                                  <div className={styles.label}>Código NCM</div>
                                  <div className={styles.valorView}>
                                    <span className={styles.text}>{ncm?.codigoNcm}</span>
                                  </div>
                                </Col>
                                <Col>
                                  <div className={styles.label}>Valor da Aquisição (R$)</div>
                                  <div className={styles.valorView}>
                                    <span className={styles.text}>{formatCurrency(ncm?.valorAquisicao)}</span>
                                  </div>
                                </Col>
                                <Col>
                                  <div className={styles.label}>Quantidade</div>
                                  <div className={styles.valorView}>
                                    <span className={styles.text}>{formatNumber(ncm?.quantidade)}</span>
                                  </div>
                                </Col>
                              </Row>
                              <Row className={styles.contentRow}>
                                <Col>
                                  <div className={styles.label}>Unidade de Medida</div>
                                  <div className={styles.valorView}>
                                    <span className={styles.text}>{ncm?.unidadeMedida}</span>
                                  </div>
                                </Col>
                                <Col>
                                  <div className={styles.label}>Preço Unitário (R$)</div>
                                  <div className={styles.valorView}>
                                    <span className={styles.text}>{formatCurrency(ncm?.precoUnitario || 0)}</span>
                                  </div>
                                </Col>
                                <Col>
                                  <div className={styles.label}>Base de Cálculo (R$)</div>
                                  <div className={styles.valorView}>
                                    <span className={styles.text}>{formatCurrency(ncm?.valorBaseCalculo || 0)}</span>
                                  </div>
                                </Col>
                              </Row>
                              <div className={styles.divider}>
                                <BrDivider />
                              </div>
                              <div className={styles.sectionTitle}>Deduções</div>
                              {ncm?.tributosIncidentes && ncm?.tributosIncidentes.length > 0 ? (
                                <>
                                  {ncm?.tributosIncidentes.map((deducao, index) => (
                                    <Row key={index} className={styles.deducoesRow}>
                                      <Col>
                                        <span className={styles.label}>{deducao.nome}</span>
                                      </Col>
                                      <Col>
                                        <span className={styles.text}>{formatCurrency(deducao.valor ?? 0)}</span>
                                      </Col>
                                      <Col></Col>
                                    </Row>
                                  ))}
                                  <Row className={styles.total}>
                                    <Col>Total:</Col>
                                    <Col>{formatCurrency(ncm?.valorTributos)}</Col>
                                    <Col></Col>
                                  </Row>
                                </>
                              ) : (
                                <span className={styles.noDeductions}>Nenhuma dedução cadastrada.</span>
                              )}
                              <div className={styles.divider}>
                                <BrDivider />
                              </div>
                              <div className={styles.sectionTitle}>Modalidades de Afetação</div>
                              {ncm?.modalidadesAfetacaoFerrovia && ncm.modalidadesAfetacaoFerrovia.length > 0 && (
                                <div className={styles.tableContainer}>
                                  <div className={styles.modalidadeTitle}>Ferrovia</div>
                                  <Table
                                    showTopBar={false}
                                    showPageSelector={false}
                                    showPagination={false}
                                    density='small'
                                  >
                                    <thead>
                                      <tr>
                                        <th>Ferrovia</th>
                                        <th>Cidade/UF Origem</th>
                                        <th>Cidade/UF Destino</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {ncm.modalidadesAfetacaoFerrovia.map((ferrovia, index) => (
                                        <tr key={index}>
                                          <td>{ferrovia?.ferrovia?.nome + ' - ' + ferrovia?.ferrovia?.sigla}</td>
                                          <td>
                                            {ferrovia?.municipioOrigem?.nome + '/' + ferrovia?.municipioOrigem?.uf}
                                          </td>
                                          <td>
                                            {ferrovia?.municipioDestino?.nome + '/' + ferrovia?.municipioDestino?.uf}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </Table>
                                </div>
                              )}
                              {ncm?.possuiAfetacaoDutovia && (
                                <div className={styles.tableContainer}>
                                  <div className={styles.modalidadeTitle}>Dutovia</div>
                                  <span className={styles.value}>Possui afetação por dutovia</span>
                                </div>
                              )}
                              {ncm?.modalidadesAfetacaoPorto && ncm.modalidadesAfetacaoPorto.length > 0 && (
                                <div className={styles.tableContainer}>
                                  <div className={styles.modalidadeTitle}>Operações Portuárias</div>
                                  <Table
                                    showTopBar={false}
                                    showPageSelector={false}
                                    showPagination={false}
                                    density='small'
                                  >
                                    <thead>
                                      <tr>
                                        <th>Município/UF do Porto</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {ncm.modalidadesAfetacaoPorto.map((operacao, index) => (
                                        <tr key={index}>
                                          <td>{operacao?.porto.cidade + '/' + operacao?.porto.uf}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </Table>
                                </div>
                              )}
                              {ncm?.modalidadesAfetacaoMunicipioMineracao &&
                                ncm.modalidadesAfetacaoMunicipioMineracao.length > 0 && (
                                  <div className={styles.tableContainer}>
                                    <div className={styles.modalidadeTitle}>Estruturas fora do município produtor</div>
                                    <Table
                                      showTopBar={false}
                                      showPageSelector={false}
                                      showPagination={false}
                                      density='small'
                                    >
                                      <thead>
                                        <tr>
                                          <th>UF da Estrutura</th>
                                          <th>Município da Estrutura</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {ncm.modalidadesAfetacaoMunicipioMineracao.map((estrutura, index) => (
                                          <tr key={index}>
                                            <td>{estrutura?.municipioMineracao?.uf}</td>
                                            <td>{estrutura?.municipioMineracao?.nome}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </Table>
                                  </div>
                                )}
                              {ncm?.modalidadesAfetacaoFerrovia.length === 0 &&
                                ncm?.possuiAfetacaoDutovia === false &&
                                ncm?.modalidadesAfetacaoPorto.length === 0 &&
                                ncm?.modalidadesAfetacaoMunicipioMineracao.length === 0 && (
                                  <span className={styles.noModalities}>
                                    Nenhuma modalidade de afetação cadastrada.
                                  </span>
                                )}
                              <div className={styles.divider}>
                                <BrDivider />
                              </div>
                              <Row className={styles.contentRow}>
                                <Col className={styles.dadosCol}>
                                  <div className={styles.label}>Rejeito ou Estéreis</div>
                                  <div className={styles.valorView}>
                                    <span className={styles.text}>{ncm?.isRejeito ? 'Sim' : 'Não'}</span>
                                  </div>
                                </Col>
                                <Col className={styles.dadosCol}>
                                  <div className={styles.label}>Uso Específico</div>
                                  <div className={styles.valorView}>
                                    <span className={styles.text}>{ncm?.hasUsoEspecifico ? 'Sim' : 'Não'}</span>
                                  </div>
                                </Col>
                                <Col className={styles.dadosCol}>
                                  <div className={styles.label}>Tipo de Uso</div>
                                  <div className={styles.valorView}>
                                    <span className={styles.text}>{ncm?.tipoDeUso?.nome}</span>
                                  </div>
                                </Col>
                              </Row>
                              <Row className={styles.contentRow}>
                                <Col>
                                  <div className={styles.label}>Valor da CFEM (R$)</div>
                                  <div className={styles.valorView}>
                                    <span className={styles.text}>{formatCurrency(ncm?.valorCfem || 0)}</span>
                                  </div>
                                </Col>
                                <Col>
                                  <div className={styles.label}>Tipo da Alíquota</div>
                                  <div className={styles.valorView}>
                                    <span className={styles.text}>{ncm.tipoAliquota}</span>
                                  </div>
                                </Col>
                                <Col>
                                  <div className={styles.label}>Alíquota (%)</div>
                                  <div className={styles.valorView}>
                                    <span className={styles.text}>{formatPercentual(ncm?.valorAliquota || 0)}</span>
                                  </div>
                                </Col>
                              </Row>
                            </BaseDeCalculoAccordion>
                          ))}
                        </>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </BrCard>
        )}
      </div>
    </div>
  );
};

export default DadosOperacoes;
